import cn from 'classnames'
import { useCallback, useMemo } from 'react'

import { TSegmentConrolCommon } from 'cng-common/components/segmentedControl/models'
import { Translation } from 'cng-common/features/translations'
import { Tooltip } from 'core-components/library/tooltip'
import { useCurrencyRestriction } from 'partner360/components/currenciesButtons/hooks/useCurrencyRestriction'
import { CURRENCY_TRANSLATION_KEYS } from 'partner360/components/currenciesButtons/translations'
import { RowButtons } from 'partner360/components/rowButtons/RowButtons'

export interface IProps {
  localCurrency: string
  currency: string
  setCurrency: (newValue: string) => void
  className?: string
  isShowOneCurrency?: boolean
  isSingleCurrencyMode?: boolean
  disabled?: boolean
  isIgnoreMultiLocalCurrencies?: boolean
  size?: TSegmentConrolCommon['size']
}

export const PartnerCurrencyButtons = ({
  localCurrency,
  currency,
  setCurrency,
  className,
  isShowOneCurrency = true,
  isSingleCurrencyMode = false,
  disabled = false,
  size,
  isIgnoreMultiLocalCurrencies = false,
}: IProps) => {
  const { isMultiLocalCurrencyLocked, changeCurrency } = useCurrencyRestriction(
    currency,
    setCurrency,
    isIgnoreMultiLocalCurrencies
  )
  const isDisabled = disabled || isMultiLocalCurrencyLocked
  const handleCurrencyChange = useCallback(
    (value: string) => {
      if (!isDisabled) changeCurrency(value)
    },
    [isDisabled, changeCurrency]
  )
  const isShowLocalCurrency = !!localCurrency && localCurrency !== 'USD'
  const isShowCurrencyControl = isShowLocalCurrency || isShowOneCurrency
  const isShowUSDCurrency = !isSingleCurrencyMode || !isShowLocalCurrency

  const options = useMemo<string[]>(() => {
    if (isMultiLocalCurrencyLocked)
      return Array.from(new Set(['USD', ...(isShowLocalCurrency ? [localCurrency] : [])]))
    if (!isShowCurrencyControl) {
      return []
    }

    const result: string[] = []
    if (isShowUSDCurrency) {
      result.push('USD')
    }
    if (isShowLocalCurrency) {
      result.push(localCurrency)
    }

    return Array.from(new Set(result))
  }, [
    isShowCurrencyControl,
    isShowUSDCurrency,
    isShowLocalCurrency,
    localCurrency,
    isMultiLocalCurrencyLocked,
  ])

  const value = isMultiLocalCurrencyLocked
    ? 'USD'
    : options.includes(currency)
      ? currency
      : options[0]

  if (!options.length) {
    return null
  }

  const control = (
    <RowButtons
      value={value}
      options={options}
      containerClassName={cn(className)}
      generateTranslationKey={(val) => val}
      onOptionClick={handleCurrencyChange}
      isDisabled={isDisabled}
      size={size}
    />
  )

  return isMultiLocalCurrencyLocked ? (
    <Tooltip
      inline
      tipContent={() => <Translation text={CURRENCY_TRANSLATION_KEYS.multipleLocalCurrencies} />}
    >
      {control}
    </Tooltip>
  ) : (
    control
  )
}
